import QRCode from 'qrcode';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { isVendor, staffSession } from '../lib/owner';
import { Shell } from './Shell';
import { handleAuthError, useStaff } from './useStaff';

interface Table { id: string; name: string; seats: number; qrVersion: number }
interface Qr { tableId: string; name: string; url: string; svg: string }

/**
 * /tables: add/rename tables and print one QR card per table (re-issue a QR to retire an old print). Vendors get one
 * stall sign first: their takeout QR, which customers scan to order at the counter (tables stay optional).
 */
export function Tables({ path }: { path: string }) {
  const [tables, setTables] = useState<Table[]>([]);
  const [qrs, setQrs] = useState<Record<string, Qr>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const vendor = isVendor(staffSession());
  const [stall, setStall] = useState<{ svg: string; on: boolean } | null>(null);
  const { s, online } = useStaff(() => undefined, () => undefined);
  const token = s?.token ?? '';

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const t = (await api<{ tables: Table[] }>('GET', '/tables', { token })).tables;
      setTables(t);
      const list = await Promise.all(t.map(async (x) => {
        const q = await api<{ tableId: string; name: string; url: string }>('GET', `/tables/${x.id}/qr`, { token });
        return { ...q, svg: await QRCode.toString(q.url, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' }) };
      }));
      setQrs(Object.fromEntries(list.map((q) => [q.tableId, q])));
      if (vendor) {
        const [r, l] = await Promise.all([
          api<{ settings: { takeoutEnabled: boolean } }>('GET', '/restaurant', { token }),
          api<{ url: string }>('GET', '/takeout/link', { token }),
        ]);
        setStall({ on: r.settings.takeoutEnabled, svg: await QRCode.toString(l.url, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' }) });
      }
    } catch (e) { handleAuthError(e); setErr((e as Error).message); }
  }, [token, vendor]);
  useEffect(() => { void load(); }, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); await load(); } catch (e) { handleAuthError(e); setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Shell path={path} online={online} sub={vendor ? 'stall QR' : `${tables.length} tables`}>
      <div className="panel row no-print" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700 }}>{vendor ? 'พิมพ์ QR ติดหน้าร้าน · Print your stall QR' : 'พิมพ์ QR วางบนโต๊ะ · Print a QR card for each table'}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-dark" disabled={busy} onClick={() => {
            const name = prompt('ชื่อโต๊ะ · Table name (e.g. A1)');
            if (name?.trim()) void run(() => api('POST', '/tables', { token, body: { name: name.trim(), sort: tables.length + 1 } }));
          }}>{vendor ? '+ มีที่นั่ง · Add a table' : '+ เพิ่มโต๊ะ · Add table'}</button>
          <button className="btn-mango" onClick={() => print()}>พิมพ์ · Print</button>
        </div>
      </div>
      {err && <div className="warn no-print" role="alert" style={{ color: 'var(--ink)', marginBottom: 12 }}>{err}</div>}
      {vendor && stall && !stall.on && (
        <div className="warn no-print" role="status" style={{ color: 'var(--ink)', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>QR ร้านยังสั่งไม่ได้ เพราะปิดรับออร์เดอร์ออนไลน์อยู่ · Your stall QR can't take orders while online ordering is off.</span>
          <button className="btn-mango" disabled={busy} onClick={() => void run(() => api('PUT', '/restaurant/settings', { token, body: { takeoutEnabled: true } }))}>เปิดรับ · Turn on</button>
        </div>
      )}
      <div className="qr-sheet">
        {vendor && (
          <article className="qr-card stall">
            <div className="display" style={{ fontSize: 30, lineHeight: 1.05 }}>{staffSession()?.name}</div>
            {stall ? <div className="qr" style={{ width: 220, border: '3px solid var(--ink)' }} role="img" aria-label="Stall QR: scan to order" dangerouslySetInnerHTML={{ __html: stall.svg }} /> : <div className="spinner" />}
            <div className="display" style={{ fontSize: 20 }}>สแกนสั่ง รับที่ร้าน</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Scan to order · pick up here</div>
          </article>
        )}
        {tables.map((t) => (
          <article key={t.id} className="qr-card">
            <div className="display" style={{ fontSize: 15 }}>{staffSession()?.name}</div>
            <div className="display" style={{ fontSize: 34, lineHeight: 1 }}>โต๊ะ {t.name}</div>
            {qrs[t.id] ? <div className="qr" style={{ width: 180, border: '3px solid var(--ink)' }} role="img" aria-label={`QR code for table ${t.name}`} dangerouslySetInnerHTML={{ __html: qrs[t.id].svg }} /> : <div className="spinner" />}
            <div style={{ fontSize: 13, fontWeight: 600 }}>สแกนเพื่อสั่งอาหาร · Scan to order</div>
            <div className="row no-print" style={{ gap: 6, width: '100%' }}>
              <button className="btn-dark" style={{ flex: 1, color: 'var(--ink)', borderColor: 'var(--ink)' }} disabled={busy} onClick={() => {
                const name = prompt('ชื่อใหม่ · New name', t.name);
                if (name?.trim()) void run(() => api('PUT', `/tables/${t.id}`, { token, body: { name: name.trim(), seats: t.seats } }));
              }}>Rename</button>
              <button className="btn-dark" style={{ flex: 1, color: 'var(--ink)', borderColor: 'var(--ink)' }} disabled={busy} title="Old printed QR for this table will stop working" onClick={() => {
                if (confirm(`New QR for table ${t.name}? The old printed QR will stop working.`)) void run(() => api('POST', `/tables/${t.id}/qr/rotate`, { token }));
              }}>New QR</button>
            </div>
          </article>
        ))}
      </div>
    </Shell>
  );
}
